import { type Component, Match, Switch } from "solid-js";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import { CompanyInfoSheet } from "./company-info-sheet";
import type { CompanyInfoScreen } from "./state";
import {
  CompanyInfoLoadError,
  CompanyInfoSkeleton,
  CompanyInfoUnavailable,
} from "./states";

/**
 * The last link of the chain: screen name in, component out.
 *
 * It lives here rather than inline in `detail-sheet.tsx` because that file has
 * no test and cannot easily get one — mounting it drags in the whole offer
 * detail, its queries and a Kobalte sheet. The two halves on either side were
 * already covered (`resolveCompanyInfoState` and `companyInfoScreen` have a node
 * test, the three screens have a jsdom one) and the seam between them had
 * nothing: swapping a `<Match>` for a condition of its own — say
 * `state.kind === "error"` instead of the screen name — put «Riprova» on a 404
 * with the whole suite still green.
 *
 * So no `<Match>` here carries a condition of its own. Each one tests the screen
 * name against a value the pure function already decided, and the part worth
 * protecting is that a 404 and a missing company id share the unavailability
 * screen: nothing to do about either, so no «Riprova» (EP3 vs EP2).
 *
 * `onRetry` is the parent's refetch, deliberately: the query lives above the
 * nested sheet, so closing and reopening it does not run again — only this
 * button does (EP2).
 */
export const CompanyInfoScreenSwitch: Component<{
  screen: CompanyInfoScreen;
  company: CompanyV2 | null;
  onRetry: () => void;
}> = (props) => (
  <Switch fallback={<CompanyInfoUnavailable />}>
    <Match when={props.screen === "skeleton"}>
      <CompanyInfoSkeleton />
    </Match>
    <Match when={props.screen === "retry"}>
      <CompanyInfoLoadError onRetry={props.onRetry} />
    </Match>
    {/*
      Both halves, and not just the screen name: `"sheet"` is the only screen
      that needs a value to render, and the fallback is the honest answer when
      the name says sheet and the company is somehow absent.
    */}
    <Match when={props.screen === "sheet" && props.company}>
      {(company) => <CompanyInfoSheet company={company()} />}
    </Match>
  </Switch>
);
